import React, { useContext, useMemo, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  Input,
  Flex,
  Icon,
  Link,
  useToast,
} from '@chakra-ui/react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { SocialChannel } from '@foodfluence/shared';
import { CollabDataContext } from 'contexts/collabDataContext';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { deliverableApi } from 'services/api/deliverable';
import { getInfluencerPrimaryHandle } from 'utils/influencerUtils';
import { getChannelDisplayName, getChannelIcon } from 'utils/channelUtils';
import { getUnitRelatedLinkRefs, DeliverableLinkRef } from 'utils/contentSubmissionUtils';
import ApprovedContentPreview from './ApprovedContentPreview';
import BottomSheet from './BottomSheet';
import BrandFeedbackBody from './BrandFeedbackBody';
import { SheetButton } from './counter-flow/sheetParts';
import { SubmissionSheetTitle, StatusPill, PillTone, linkInputProps } from './submissionSheetParts';
import { ff } from '../../../theme';

interface InputLinkUgcModalProps {
  isOpen: boolean;
  onClose: () => void;
  requirementId: string;
  modalHeader: string;
  clientName: string;
  clientId: string;
  /** Approved UGC asset for this content piece, shown so it can be downloaded and posted. */
  approvedContentUrl?: string;
}

const refKey = (ref: DeliverableLinkRef) => `${ref.deliverableKey}:${ref.linkIndex}`;

const placeholderFor = (deliverableKey: string): string => {
  switch (deliverableKey) {
    case 'tiktoks': return 'Paste TikTok link...';
    case 'instagramReels': return 'Paste Instagram Reel link...';
    case 'instagramPosts': return 'Paste Instagram Post link...';
    case 'instagramStories': return 'Paste Instagram Story link...';
    default: return 'Paste link here...';
  }
};

const InputLinkUgcModal: React.FC<InputLinkUgcModalProps> = ({
  isOpen,
  onClose,
  requirementId,
  modalHeader,
  clientName,
  clientId,
  approvedContentUrl,
}) => {
  const toast = useToast();
  const location = useLocation();
  const collabId = new URLSearchParams(location.search).get('id');
  const collabData = useContext(CollabDataContext);
  const collab = collabData.find(c => c.id === collabId);
  const { influencerData } = useContext(InfluencerDataContext);

  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reactive: recomputed as the collab updates after each submission.
  const relatedRefs = useMemo(
    () =>
      getUnitRelatedLinkRefs(requirementId, collab?.deliverableLinks || {})
        .sort((a, b) => a.deliverableKey.localeCompare(b.deliverableKey)),
    [collab?.deliverableLinks, requirementId],
  );

  const isEditable = (ref: DeliverableLinkRef) =>
    ref.link.status === 'pendingSubmission' || Boolean(ref.link.rejectionReason);

  const filledEditableRefs = relatedRefs.filter(
    ref => isEditable(ref) && (inputs[refKey(ref)] || '').trim(),
  );

  const handleClose = () => {
    setInputs({});
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmitAll = async () => {
    if (filledEditableRefs.length === 0) {
      toast({ title: 'Add at least one link.', description: 'Enter a link for a platform before submitting.', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    if (!collab) {
      toast({ title: 'Error', description: 'Collaboration data not found.', status: 'error', duration: 3000, isClosable: true });
      return;
    }

    setIsSubmitting(true);
    try {
      for (const ref of filledEditableRefs) {
        await deliverableApi.submitPost({
          collabId: collab.id,
          deliverableKey: ref.deliverableKey,
          linkIndex: ref.linkIndex,
          postUrl: (inputs[refKey(ref)] || '').trim(),
          influencerHandle: getInfluencerPrimaryHandle(influencerData),
          clientName,
          clientId,
        }, influencerData?.id);
      }
      toast({
        title: filledEditableRefs.length > 1 ? 'Links submitted.' : 'Link submitted.',
        description: 'Your live post links have been submitted for review.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      handleClose();
    } catch (error) {
      toast({ title: 'Submission failed.', description: 'There was an error submitting your links.', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusPill = (ref: DeliverableLinkRef): { label: string; tone: PillTone } | null => {
    if (ref.link.rejectionReason) return { label: 'Needs update', tone: 'red' };
    if (ref.link.status === 'approved') return { label: 'Approved', tone: 'green' };
    if (ref.link.status === 'pendingVerification') return { label: 'Under review', tone: 'grey' };
    return null;
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose}>
      <Box overflowY="auto" px="20px" pt={1} pb="28px" fontFamily="display" color={ff.ink}>
        <SubmissionSheetTitle title={modalHeader} context="Submit your live post link for each platform" />

        <VStack spacing="12px" align="stretch">
          {approvedContentUrl && (
            <ApprovedContentPreview url={approvedContentUrl} fileNameBase={modalHeader} />
          )}

          {relatedRefs.length === 0 && (
            <Text fontSize="14px" color="#55555A" textAlign="center" py={4}>
              No platforms to submit links for.
            </Text>
          )}

          {relatedRefs.map((ref) => {
            const key = refKey(ref);
            const editable = isEditable(ref);
            const pill = statusPill(ref);
            const channel = ref.deliverableKey as SocialChannel;
            const ChannelIcon = getChannelIcon(channel);

            return (
              <Box
                key={key}
                bg="white"
                borderRadius="18px"
                p="14px"
                border="1px solid"
                borderColor={ref.link.rejectionReason ? '#F0B8AC' : '#E4E4E0'}
              >
                <Flex align="center" justify="space-between" gap="12px" mb="10px">
                  <Flex align="center" gap="12px" minW={0}>
                    <Flex flex="none" w="44px" h="44px" borderRadius="12px" bg={ff.grey} align="center" justify="center">
                      <Icon as={ChannelIcon} color={ff.ink} boxSize="20px" />
                    </Flex>
                    <Text fontSize="16px" fontWeight={600} isTruncated>
                      {getChannelDisplayName(channel)}
                    </Text>
                  </Flex>
                  {pill && <StatusPill tone={pill.tone}>{pill.label}</StatusPill>}
                </Flex>

                {ref.link.rejectionReason && (
                  <Box bg="#FBE1DC" borderRadius="12px" px="12px" py="10px" mb="10px">
                    <BrandFeedbackBody feedback={ref.link.rejectionReason} compact />
                  </Box>
                )}

                {editable ? (
                  <Input
                    value={inputs[key] ?? ''}
                    onChange={(e) => setInputs(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder={placeholderFor(ref.deliverableKey)}
                    aria-label={`${getChannelDisplayName(channel)} post link`}
                    {...linkInputProps(false)}
                  />
                ) : (
                  ref.link.userLink && (
                    <Link
                      href={ref.link.userLink}
                      isExternal
                      color={ff.ink}
                      fontWeight={600}
                      textDecoration="underline"
                      fontSize="14px"
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      maxW="100%"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      <Text as="span" isTruncated minW={0}>
                        {ref.link.userLink}
                      </Text>
                      <Icon as={FaExternalLinkAlt} boxSize={2.5} flexShrink={0} />
                    </Link>
                  )
                )}
              </Box>
            );
          })}

          <SheetButton
            tone="lime"
            onClick={handleSubmitAll}
            isDisabled={filledEditableRefs.length === 0 || isSubmitting}
            isLoading={isSubmitting}
            w="full"
            fontSize="16px"
            mt="4px"
          >
            {filledEditableRefs.length > 1 ? `Submit ${filledEditableRefs.length} links` : 'Submit link'}
          </SheetButton>
        </VStack>
      </Box>
    </BottomSheet>
  );
};

export default InputLinkUgcModal;
